
























































































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";
import PriceListsStore from "@/mixins/priceListsStore";

// Types
import { IProduct } from "@/models/products";
import { IPromotion, IPromotionProduct } from "@/models/promotions";
import { DiscountTypes } from "@/data/enums/promotions";
import { DataModules } from "@/store/modules/data";

@Component({
  components: {
    "empty-image": () =>
      import("@/components/base/ImageGallery/emptyPreviewImage.vue"),
    "promotion-tags": () =>
      import("@/components/app/admin/catalogue/promotions/promotionTags.vue"),
    "individual-promotions-vessels": () =>
      import(
        "@/components/app/admin/catalogue/promotions/individualPromotionsVessels.vue"
      )
  }
})
export default class AttachedPromotionProducts extends Mixins(
  RequestPromises,
  BrandsStore,
  CurrenciesStore,
  BillingCyclesStore,
  PriceListsStore
) {
  // Props
  @Prop({ default: null })
  public promotion!: IPromotion | null;

  @Prop({ required: true })
  public value!: IPromotionProduct[];

  @Prop({ required: true })
  public isDisabled!: boolean;

  @Prop({ required: true })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public isReorderable!: boolean;

  @Prop({ required: true })
  public canManagePromotion!: boolean;

  @Prop({ required: true })
  public typeIsChanged!: boolean;

  // Data
  public isLoading = false;
  public isLoadingMore = false;
  public isReloading = false;
  public requestError = false;

  public selectedProducts: IProduct[] = [];
  public formProducts = this.value;

  // Getters
  public get guardFunctionalities() {
    return ["staff_get_product", "list_products", "list_product_categories"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get isUpdate() {
    return !this.$_.isEmpty(this.promotion);
  }

  public get formProductsIds() {
    return this.$_.map(
      this.formProducts,
      formProduct => formProduct.product_id
    );
  }

  // Lifecycle methods
  public async created() {
    this.formProducts = this.value;

    if (!this.contentIsAvailable) return;

    this.isLoading = true;

    await Promise.all([
      this.getCurrencies(),
      this.getBillingCycles(),
      this.getPriceLists()
    ]);

    this.loadData();
  }

  // Methods
  public async loadData() {
    try {
      await this.loadMoreProducts();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getNewProducts(newProductsIds: string) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/list`, {
      params: {
        limit: 0,
        "filter[id|like]": newProductsIds,
        with: "image"
      },
      storeData: false,
      returnData: true,
      vm: this
    });
  }

  public async loadMoreProducts() {
    try {
      this.isLoadingMore = true;

      const newProductsIds = this.$_.filter(
        this.formProductsIds,
        productId => !this.$_.some(this.selectedProducts, ["id", productId])
      ).join(",");

      if (!newProductsIds.length) {
        return Promise.resolve();
      }
      const newProducts = await this.getNewProducts(newProductsIds);
      this.selectedProducts = this.$_.concat(
        this.selectedProducts,
        newProducts
      );
      this.requestError = false;
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoadingMore = false;
    }
  }

  public get sortableData() {
    return this.$_.orderBy(
      this.$_.map(this.formProducts, formProduct => {
        return this.$_.merge(
          {},
          formProduct,
          this.$_.pick(
            this.$_.find(this.selectedProducts, ["id", formProduct.product_id]),
            ["name", "category", "image", "product_type"]
          )
        );
      }),
      ["order", "name"],
      ["asc", "asc"]
    );
  }

  public async updateIndividualProduct(promotionProduct: IPromotionProduct) {
    this.$emit("updateIndividualProduct", promotionProduct);
  }

  public async duplicateIndividualProduct(promotionProduct: IPromotionProduct) {
    this.$emit("duplicateIndividualProduct", promotionProduct);
  }

  public detachIndividualProduct(promotionProduct: IPromotionProduct) {
    this.$emit("detachIndividualProducts", [promotionProduct]);
  }

  public attachIndividualProduct() {
    if (this.isDisabled || this.isProcessing) return;
    this.$emit("attachIndividualProduct");
  }

  public getAmount(product) {
    if (product.type === DiscountTypes.PERCENTAGE_BASED) {
      return `${product.amount}%`;
    }
    const currency = this.$_.find(this.currencies, ["id", product.currency_id]);
    return `${this.$_.get(currency, "prefix", "")}${
      product.amount
    }${this.$_.get(currency, "suffix", "")}`;
  }

  public getProductCurrencies(product) {
    const currencies = this.$_.get(product, "currency_id")
      ? [this.$_.get(product, "currency_id")]
      : this.$_.get(product, "currencies", []);

    return this.$_.map(currencies, productCurrencyId => {
      return this.$_.get(
        this.$_.find(this.currencies, ["id", productCurrencyId]),
        "code",
        ""
      );
    });
  }

  public getPriceListsNames(pricelists) {
    return this.$_.map(pricelists, priceListId => {
      return this.$_.get(
        this.$_.find(this.priceLists, ["id", priceListId]),
        "name",
        ""
      );
    });
  }

  public getBillingTermsNames(billingTerms) {
    return this.$_.map(billingTerms, billingTerm => {
      return this.$_.get(
        this.$_.find(this.billingCycles, ["months", billingTerm]),
        "name",
        this.$t("_.every_months", { months: billingTerm })
      );
    });
  }

  public async reorder(movedElementsIds: string[]) {
    if (!this.isReorderable) return;
    if (!this.isUpdate || this.typeIsChanged) {
      const formProducts = this.$_.map(this.formProducts, formProduct => {
        formProduct.order = this.$_.findIndex(
          movedElementsIds,
          movedElementsId => movedElementsId === formProduct.id
        );

        return formProduct;
      });
      this.$emit("input", formProducts);
      return;
    }

    this.$emit("reorderIndividualProducts", movedElementsIds);
  }

  @Watch("formProductsIds")
  public onFormProductsIdsChange() {
    this.loadMoreProducts();
  }

  @Watch("value", { deep: true })
  public onValueChange(value) {
    this.formProducts = value;
  }
}
